import { type DayRange, formatMinuteOfDay, mergeRanges, WEEKDAYS_SHORT_PT } from "@byfluent/domain";
import { useMemo, useRef } from "react";

export interface LockedSlot extends DayRange {
  label: string;
  status: "held" | "active";
}

const ORDER = [1, 2, 3, 4, 5, 6, 0];
const key = (weekday: number, minute: number) => `${weekday}:${minute}`;

export function rangesToCells(ranges: DayRange[], step: number): Set<string> {
  const s = new Set<string>();
  for (const r of ranges) for (let m = r.start; m < r.end; m += step) s.add(key(r.weekday, m));
  return s;
}

export function cellsToRanges(cells: Set<string>, step: number): DayRange[] {
  const out: DayRange[] = [];
  for (const k of cells) {
    const [w, m] = k.split(":").map(Number) as [number, number];
    out.push({ weekday: w, start: m, end: m + step });
  }
  return mergeRanges(out);
}

/**
 * Grade semanal em passos de 30 minutos: toque ou arraste para marcar.
 * Faixas com aluno (reservado ou ativo) aparecem travadas com o nome.
 */
export function WeekGrid({
  value,
  onChange,
  preferred,
  onPreferredChange,
  paintPreferred = false,
  locked = [],
  startHour = 6,
  endHour = 23,
  step = 30,
  readOnly = false,
}: {
  value: DayRange[];
  onChange?: (ranges: DayRange[]) => void;
  preferred?: DayRange[];
  onPreferredChange?: (ranges: DayRange[]) => void;
  paintPreferred?: boolean;
  locked?: LockedSlot[];
  startHour?: number;
  endHour?: number;
  step?: number;
  readOnly?: boolean;
}) {
  const cells = useMemo(() => rangesToCells(value, step), [value, step]);
  const prefCells = useMemo(() => rangesToCells(preferred ?? [], step), [preferred, step]);
  const lockedMap = useMemo(() => {
    const m = new Map<string, LockedSlot>();
    for (const l of locked) for (let t = l.start; t < l.end; t += step) m.set(key(l.weekday, t), l);
    return m;
  }, [locked, step]);
  // Arrastar seleciona o retângulo entre a célula inicial e a atual (dias x horários).
  const paint = useRef<{
    on: boolean;
    anchor: [number, number];
    base: Set<string>;
    last: string;
  } | null>(null);

  const minutes: number[] = [];
  for (let m = startHour * 60; m < endHour * 60; m += step) minutes.push(m);

  const target = paintPreferred ? prefCells : cells;

  const apply = (k: string) => {
    const p = paint.current;
    if (!p || p.last === k) return;
    p.last = k;
    const [w, m] = k.split(":").map(Number) as [number, number];
    const d0 = Math.min(p.anchor[0], ORDER.indexOf(w));
    const d1 = Math.max(p.anchor[0], ORDER.indexOf(w));
    const m0 = Math.min(p.anchor[1], m);
    const m1 = Math.max(p.anchor[1], m);
    const next = new Set(p.base);
    for (let d = d0; d <= d1; d++) {
      for (let t = m0; t <= m1; t += step) {
        const ck = key(ORDER[d] as number, t);
        if (lockedMap.has(ck) || (paintPreferred && !cells.has(ck))) continue;
        if (p.on) next.add(ck);
        else next.delete(ck);
      }
    }
    const ranges = cellsToRanges(next, step);
    if (paintPreferred) onPreferredChange?.(ranges);
    else onChange?.(ranges);
  };

  const onDown = (k: string) => {
    if (readOnly || lockedMap.has(k)) return;
    const [w, m] = k.split(":").map(Number) as [number, number];
    paint.current = {
      on: !target.has(k),
      anchor: [ORDER.indexOf(w), m],
      base: new Set(target),
      last: "",
    };
    apply(k);
  };

  const onMove = (e: React.PointerEvent) => {
    if (!paint.current) return;
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const k = el?.dataset?.cell;
    if (k) apply(k);
  };

  const stop = () => {
    paint.current = null;
  };

  return (
    <div className="week-scroll">
      <div
        className="week"
        onPointerMove={onMove}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
      >
        <div className="head" />
        {ORDER.map((w) => (
          <div key={w} className="head">
            {WEEKDAYS_SHORT_PT[w]}
          </div>
        ))}
        {minutes.map((m) => (
          <Row key={m} minute={m} step={step}>
            {ORDER.map((w) => {
              const k = key(w, m);
              const lock = lockedMap.get(k);
              const isStart = lock && lock.start === m;
              const cls = [
                "cell",
                m % 60 === 0 ? "hour" : "",
                lock
                  ? lock.status === "active"
                    ? "active-slot"
                    : "held"
                  : prefCells.has(k)
                    ? "pref"
                    : cells.has(k)
                      ? "on"
                      : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <div
                  key={k}
                  data-cell={k}
                  className={cls}
                  title={
                    lock
                      ? `${lock.label} (${lock.status === "active" ? "ativo" : "reservado"})`
                      : `${WEEKDAYS_SHORT_PT[w]} ${formatMinuteOfDay(m)}`
                  }
                  onPointerDown={(e) => {
                    e.preventDefault();
                    onDown(k);
                  }}
                >
                  {isStart ? lock.label : ""}
                </div>
              );
            })}
          </Row>
        ))}
      </div>
    </div>
  );
}

function Row({
  minute,
  step,
  children,
}: {
  minute: number;
  step: number;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="time">
        {minute % 60 === 0
          ? formatMinuteOfDay(minute)
          : step === 30
            ? ""
            : formatMinuteOfDay(minute)}
      </div>
      {children}
    </>
  );
}

export function minutesByDay(ranges: DayRange[]): Record<number, number> {
  const out: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  for (const r of mergeRanges(ranges)) out[r.weekday] = (out[r.weekday] ?? 0) + (r.end - r.start);
  return out;
}
